import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, StickyNote, ShieldCheck, MessageSquareQuote, Info } from 'lucide-react';
import { useStore, vendorStats, vendorUnchecked, nextPaymentForVendor, guardStats, fmtIDR, fmtDate } from '../lib/store';
import { VENDOR_VERIFY_CATEGORIES, VENDOR_QUESTIONS, MONEY_KEYS, VERIFY_NEXT_STEP } from '../lib/checklists';
import { BackBar, StatusChip, Money, EmptyState, inputCls, EASE } from '../components/Bits';

function VerifyItem({ vendorId, item, rec, toggleVerify, setVerifyNote, nextPay }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(rec?.note || '');
  const done = !!rec?.checked;
  const q = VENDOR_QUESTIONS[item.key];
  const isMoney = MONEY_KEYS.includes(item.key);
  const step = VERIFY_NEXT_STEP[item.key];

  return (
    <div data-testid={`verify-item-${item.key}`} className={`rounded-xl border px-4 py-3.5 transition-colors ${done ? 'border-[#CBDCCE] bg-[#EBF2EC]/50' : 'border-line bg-white'}`}>
      {!done && isMoney && nextPay && (
        <p className="mb-2 font-mono text-[10px] tnum uppercase tracking-wider text-amberw">
          Sebelum {nextPay.label} · {fmtIDR(nextPay.amount)}
        </p>
      )}
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium text-pewter leading-snug">{item.label}</p>
        <StatusChip done={done} testid={`verify-status-${item.key}`} />
      </div>

      {done && (
        <p className="mt-2 font-mono text-[10px] tnum text-sage/90">Dicek oleh Anda · {fmtDate(rec.checkedAt)}</p>
      )}
      {done && rec?.note && <p className="mt-1.5 text-xs text-cashmere italic">“{rec.note}”</p>}
      {!done && !rec?.note && <p className="mt-1.5 text-[11px] text-cashmere/80 italic">Belum ada catatan tertulis.</p>}
      {!done && rec?.note && <p className="mt-1.5 text-xs text-cashmere italic">“{rec.note}”</p>}

      {!done && step && nextPay && (
        <p className="mt-2 text-[11px] leading-relaxed text-pewter">
          <span className="font-bold text-ink">Langkah berikutnya:</span> {step.replace('{stage}', nextPay.label)}
        </p>
      )}

      {!done && q && (
        <div className="mt-2.5 flex gap-2 rounded-lg bg-bone px-3 py-2.5">
          <MessageSquareQuote size={13} className="text-terra shrink-0 mt-0.5" />
          <p className="text-[11px] text-cashmere leading-relaxed"><span className="font-semibold text-ink">Bisa ditanyakan ke vendor:</span> {q}</p>
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button
          data-testid={`verify-toggle-${item.key}`}
          onClick={() => toggleVerify(vendorId, item.key)}
          className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[11px] font-bold transition-colors ${
            done ? 'bg-white border border-ink/15 text-slate-500 hover:text-ink' : 'bg-ink text-ivory hover:bg-inkdeep'
          }`}
        >
          <Check size={12} strokeWidth={3} /> {done ? 'Batalkan Tanda' : 'Tandai Sudah Dicek'}
        </button>
        <button
          data-testid={`verify-note-btn-${item.key}`}
          onClick={() => setNoteOpen((o) => !o)}
          className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[11px] font-semibold border border-ink/12 text-slate-500 hover:text-ink bg-white transition-colors"
        >
          <StickyNote size={12} /> Catatan
        </button>
      </div>

      <AnimatePresence>
        {noteOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: EASE }} className="overflow-hidden">
            <div className="mt-3 flex gap-2">
              <input
                data-testid={`verify-note-input-${item.key}`}
                className={inputCls + ' !py-2 text-xs'}
                placeholder="Contoh: 3 acara terbaru dicatat."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <button
                data-testid={`verify-note-save-${item.key}`}
                onClick={() => { setVerifyNote(vendorId, item.key, note); setNoteOpen(false); }}
                className="shrink-0 rounded-xl bg-terra text-ivory px-3.5 text-[11px] font-bold"
              >
                Simpan
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function VendorDetail() {
  const { id } = useParams();
  const { state, toggleVerify, setVerifyNote } = useStore();
  const [openCat, setOpenCat] = useState(null);
  const vendor = state.vendors.find((v) => v.id === id);

  if (!vendor) return <EmptyState title="Vendor tidak ditemukan" desc="Mungkin data sudah direset." />;

  const rec = state.verifications[id] || {};
  const stats = vendorStats(state, id);
  const unchecked = vendorUnchecked(state, id);
  const nextSteps = unchecked.slice(0, 3);
  const nextPay = nextPaymentForVendor(state, id);
  const vendorPayments = state.payments
    .filter((p) => p.vendorId === id)
    .sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));

  return (
    <div data-testid="vendor-detail-page" className="space-y-6">
      <BackBar to="/app/vendor" title="Vendor" />

      {/* Header */}
      <div className="relative overflow-hidden rounded-[20px] bg-white border border-line soft-shadow p-5">
        <div className="kawung absolute inset-0 opacity-[0.03] pointer-events-none" />
        <div className="relative">
          <span className="rounded-full bg-bone border border-line px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-ink/60">{vendor.category}</span>
          <h1 className="mt-3 font-serif text-2xl font-medium tracking-tight leading-snug text-ink">{vendor.name}</h1>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 font-mono text-[11px] text-cashmere">
            {vendor.pic && <span>PIC {vendor.pic}</span>}
            {vendor.phone && <span>{vendor.phone}</span>}
            {vendor.location && <span>{vendor.location}</span>}
            {vendor.website && <span>{vendor.website}</span>}
          </div>
          {vendor.value > 0 && (
            <p className="mt-3 text-xs text-cashmere">Nilai kerja sama <Money className="text-ink font-semibold">{fmtIDR(vendor.value)}</Money></p>
          )}
          {vendor.notes && <p className="mt-2 text-xs text-cashmere italic">“{vendor.notes}”</p>}
        </div>
      </div>

      {/* Summary */}
      <div className="rounded-[20px] border border-line bg-white soft-shadow p-5">
        <p className="font-serif text-lg font-medium text-ink mb-1">Vendor Verify</p>
        <p className="text-xs text-cashmere leading-relaxed mb-4">Lihat apa yang sudah kamu cek, apa yang masih belum jelas, dan apa yang perlu dibereskan sebelum pembayaran berikutnya.</p>

        {nextPay && (
          <div data-testid="verify-payment-context" className="mb-4 rounded-xl bg-bone border border-line px-4 py-3.5 flex items-center justify-between gap-3">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-widest text-cashmere">DP berikutnya</p>
              <p className="mt-1 font-mono tnum text-sm font-semibold text-ink">{fmtIDR(nextPay.amount)} <span className="text-cashmere font-normal">· {fmtDate(nextPay.dueDate)}</span></p>
            </div>
            {(() => {
              const g = guardStats(state, nextPay.id);
              return g.remaining > 0 ? (
                <span className="shrink-0 rounded-full bg-[#FBF1DF] border border-[#EEDCB9] text-amberw text-[10px] font-semibold px-2.5 py-1 tnum text-center leading-tight">
                  Sebelum pembayaran ini<br />{g.remaining} hal masih perlu ditinjau
                </span>
              ) : (
                <span className="shrink-0 rounded-full bg-[#EBF2EC] border border-[#CBDCCE] text-sage text-[10px] font-semibold px-2.5 py-1">Checklist pembayaran sudah ditinjau</span>
              );
            })()}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-[#EBF2EC] border border-[#CBDCCE] px-4 py-3">
            <p data-testid="verify-checked-count" className="font-serif text-2xl font-medium text-sage">{stats.checked} <span className="text-sm">hal</span></p>
            <p className="text-[11px] font-semibold text-sage/80">Sudah Dicek</p>
          </div>
          <div className="rounded-xl bg-[#FBF1DF] border border-[#EEDCB9] px-4 py-3">
            <p data-testid="verify-remaining-count" className="font-serif text-2xl font-medium text-amberw">{stats.remaining} <span className="text-sm">hal</span></p>
            <p className="text-[11px] font-semibold text-amberw/80">Perlu Dicek</p>
          </div>
        </div>
      </div>

      {/* Evidence ledger */}
      <div className="space-y-3">
        {VENDOR_VERIFY_CATEGORIES.map((cat, ci) => {
          const doneCount = cat.items.filter((i) => rec[i.key]?.checked).length;
          const open = openCat === cat.id || (openCat === null && ci === 0);
          return (
            <div key={cat.id} className="rounded-2xl border border-ink/10 bg-white overflow-hidden">
              <button
                data-testid={`verify-cat-${cat.id}`}
                onClick={() => setOpenCat(open ? '_closed' : cat.id)}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[10px] text-gold">{String.fromCharCode(65 + ci)}</span>
                  <span className="text-sm font-bold text-ink">{cat.title}</span>
                </div>
                <span className="flex items-center gap-2">
                  <span className={`font-mono text-[10px] ${doneCount === cat.items.length ? 'text-sage' : 'text-slate-400'}`}>{doneCount}/{cat.items.length}</span>
                  <ChevronDown size={15} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                </span>
              </button>
              <AnimatePresence initial={false}>
                {open && (
                  <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} transition={{ duration: 0.35, ease: EASE }} className="overflow-hidden">
                    <div className="px-4 pb-4 space-y-2.5">
                      {cat.items.map((item) => (
                        <VerifyItem key={item.key} vendorId={id} item={item} rec={rec[item.key]} toggleVerify={toggleVerify} setVerifyNote={setVerifyNote} nextPay={nextPay} />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* VENDOR PAYMENTS */}
      {vendorPayments.length > 0 && (
        <div data-testid="vendor-payments" className="rounded-[20px] border border-line bg-white soft-shadow p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-terra mb-3">Pembayaran Vendor</p>
          <div className="space-y-2">
            {vendorPayments.map((p) => (
              <div key={p.id} data-testid={`vendor-payment-${p.id}`} className={`rounded-xl border px-4 py-3.5 ${p.status === 'paid' ? 'border-[#CBDCCE] bg-[#EBF2EC]/40' : 'border-line bg-white'}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-sm font-semibold text-ink">{p.label}</p>
                  <Money className="text-sm font-semibold text-ink shrink-0">{fmtIDR(p.amount)}</Money>
                </div>
                <div className="mt-1.5 flex items-center justify-between gap-3">
                  <p className="font-mono tnum text-[10px] text-cashmere">
                    {p.status === 'paid' ? `Dibayar ${fmtDate(p.paidAt)}` : `Jatuh tempo ${fmtDate(p.dueDate)}`}
                  </p>
                  {p.status === 'paid' ? (
                    <StatusChip done doneLabel="Sudah Dibayar" />
                  ) : (
                    <Link data-testid={`vendor-payment-guard-${p.id}`} to={`/app/pembayaran/${p.id}/guard`} className="inline-flex items-center gap-1.5 text-[11px] font-bold text-terra hover:underline shrink-0">
                      <ShieldCheck size={12} /> Cek Sebelum Transfer
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Next steps */}
      {nextSteps.length > 0 && (
        <div className="rounded-2xl border border-ink/10 bg-white p-5">
          <p className="font-serif text-lg font-semibold text-ink mb-3">Langkah Berikutnya</p>
          <p className="text-xs text-slate-500 mb-3">Sebelum pembayaran berikutnya, prioritaskan:</p>
          <ol className="space-y-2">
            {nextSteps.map((s, i) => (
              <li key={s.key} className="flex items-baseline gap-3 text-sm text-pewter">
                <span className="font-mono text-[10px] text-gold">0{i + 1}</span>
                {s.label}
              </li>
            ))}
          </ol>
        </div>
      )}

      {nextPay && (
        <Link
          data-testid="vendor-to-guard-cta"
          to={`/app/pembayaran/${nextPay.id}/guard`}
          className="card-lift flex items-center justify-between gap-3 rounded-2xl bg-ink text-ivory px-5 py-4"
        >
          <div className="flex items-center gap-3">
            <ShieldCheck size={18} className="text-gold" />
            <div>
              <p className="text-sm font-bold">Lanjut ke Payment Guard</p>
              <p className="text-[11px] text-ivory/60">{nextPay.label} · {fmtIDR(nextPay.amount)} · {fmtDate(nextPay.dueDate)}</p>
            </div>
          </div>
          <span className="text-terra font-bold">→</span>
        </Link>
      )}

      <Link data-testid="vendor-disclaimer-link" to="/app/tentang" className="flex items-center gap-2 text-[11px] text-slate-400 hover:text-slate-600">
        <Info size={12} /> Tentang SebelumSah & batasan verifikasi
      </Link>
    </div>
  );
}
